import { useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'

import s from './Sidebar.module.scss'

import { CustomButton } from '../CustomButton/CustomButton'

import { useAuthContext } from 'contexts/AppAuthContext'
import { useArcanaContext } from 'contexts/ArcanaAuthContext'
import { useDeviceContext } from 'contexts/DeviceContext'

import { BriefcaseIcon } from 'assets/icons/BriefcaseIcon'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { ListIcon } from 'assets/icons/ListIcon'
import { PublisherIcon } from 'assets/icons/PublisherIcon'
import { SettingsIcon } from 'assets/icons/SettingsIcon'
import { GithubIcon } from 'assets/icons/socials/GithubIcon'
import { LinkedInIcon } from 'assets/icons/socials/LinkedInIcon'
import { MediumIcon } from 'assets/icons/socials/MediumIcon'
import { TelegramIcon } from 'assets/icons/socials/TelegramIcon'
import { TwitterIcon } from 'assets/icons/socials/TwitterIcon'
import { YoutubeIcon } from 'assets/icons/socials/YoutubeIcon'
import Logo from 'assets/svg/Logo.svg'
import arcanaIcon from 'assets/svg/arcana-logo.png'
import metamaskIcon from 'assets/svg/icons-crypto-metamask.svg'

import { DropDownMenu } from 'components/UI/DropDownMenu/DropDownMenu'

const marketplaceLinks = [{ to: 'marketplace', label: 'Ad Spaces' }]

const landownerLinks = [
  { to: 'publisher/dashboard', label: 'Dashboard' },
  { to: 'publisher/my-adspaces', label: 'My Ad Spaces' },
  { to: 'publisher/scenes', label: 'My Scenes' },
  { to: 'publisher/moderation', label: 'Moderation' },
]

const advertiserLinks = [
  { to: 'advertiser/dashboard', label: 'Dashboard' },
  { to: 'advertiser/my-campaigns', label: 'My Campaigns' },
  { to: 'advertiser/my-creatives', label: 'My Creatives' },
  { to: 'advertiser/audience', label: 'Audience' },
]

const Sidebar = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { deviceType } = useDeviceContext()

  const navigate = useNavigate()
  const {
    authData: { web3token, metamaskAcc, arcanaAcc },
    setWeb3Token,
    setMetamaskAcc,
    setArcanaAcc,
  } = useAuthContext()

  const logout = () => {
    localStorage.removeItem('metamaskAcc')
    setMetamaskAcc('')
    localStorage.removeItem('web3token')
    setWeb3Token('')
    navigate('../marketplace')
    setIsOpen(false)
  }

  const [logoutLoading, setLogoutLoading] = useState(false)
  const { logout: arcanaLogout } = useArcanaContext()

  const handleArcanaLogout = async () => {
    localStorage.removeItem('web3token')
    setWeb3Token('')
    localStorage.removeItem('arcanaAcc')
    setArcanaAcc('')
    setLogoutLoading(true)
    try {
      await arcanaLogout()
      setLogoutLoading(false)
    } catch (e) {
      console.error(e)
      setLogoutLoading(false)
    }
    navigate(0)
    setIsOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      {deviceType === 'mobile' && !isOpen && (
        <div onClick={() => setIsOpen(true)} className={s.listIconWrapper}>
          <ListIcon className={s.listIcon} />
        </div>
      )}
      <div
        className={`${s.sidebarWrapper} ${
          deviceType === 'mobile' ? s['sidebarWrapper_mobile' + (isOpen ? '_open' : '_closed')] : ''
        }`}
      >
        <div>
          {deviceType === 'mobile' && !isOpen && (
            <div onClick={() => setIsOpen(true)} className={s.listIconWrapper}>
              <ListIcon className={s.menuIcon} />
            </div>
          )}
          {deviceType === 'mobile' && (
            <div onClick={() => setIsOpen(false)}>
              <CloseIcon className={s.closeIcon} />
            </div>
          )}
          <div className={s.sidebarHeader}>
            <Link to="publisher/my-adspaces">
              <img src={Logo} alt="" className={s.logo} />
            </Link>
          </div>

          <div className={s.sidebarContent}>
            <DropDownMenu
              isLoggedIn={true}
              links={marketplaceLinks.map((link) => ({ ...link, onLinkClick: () => setIsOpen(false) }))}
              title="Marketplace"
              icon={<BriefcaseIcon />}
              variant="primary"
              withoutLogin
            />
            <DropDownMenu
              isLoggedIn={isLoggedIn}
              links={landownerLinks.map((link) => ({ ...link, onLinkClick: () => setIsOpen(false) }))}
              title="Landowner"
              icon={<PublisherIcon />}
              variant="primary"
              onMenuClick={() => !isLoggedIn && setIsOpen(false)}
            />
            <DropDownMenu
              isLoggedIn={isLoggedIn}
              links={advertiserLinks.map((link) => ({ ...link, onLinkClick: () => setIsOpen(false) }))}
              title="Advertiser"
              icon={<BriefcaseIcon />}
              variant="secondary"
              onMenuClick={() => !isLoggedIn && setIsOpen(false)}
            />
            {/* <CustomTooltip title="Coming soon">
              <DropDownMenu
                isLoggedIn={isLoggedIn}
                links={advertiserLinks.map((link) => ({ ...link, onLinkClick: () => setIsOpen(false) }))}
                title="Citizen"
                icon={<EyeIcon />}
                variant="secondary"
                disabled
                width={180}
              />
            </CustomTooltip> */}
          </div>
        </div>
        <div className={s.footer}>
          {isLoggedIn && (
            <div className={`${s.dropLinks} ${s.settingsWrapper}`}>
              <div className={`${s.sidebarButton}`}>
                <NavLink
                  to="settings"
                  className={({ isActive }) => (isActive ? `${s.settingsLink} ${s.active}` : s.settingsLink)}
                  onClick={() => setIsOpen(false)}
                >
                  <SettingsIcon className={s.iconImg} />
                  Settings
                </NavLink>
              </div>
            </div>
          )}
          {deviceType === 'mobile' && (
            <div>
              {!web3token ? (
                <CustomButton
                  onClick={() => {
                    setIsOpen(false)
                    navigate('/login')
                  }}
                  variant="outlined"
                  className={s.connectButton}
                  size="240"
                >
                  Connect
                </CustomButton>
              ) : null}

              <div className={s.buttonsWrapper}>
                {web3token && metamaskAcc && (
                  <>
                    <CustomButton
                      variant="outlined"
                      onClick={() => {
                        setIsOpen(false)
                        navigate('../settings')
                      }}
                      size="240"
                      className={s.disconnectButton}
                    >
                      <img className={s.metamaskIcon} src={metamaskIcon} alt="" />
                      <span className={s.cropTextButton}>{metamaskAcc}</span>
                    </CustomButton>
                    <CustomButton variant="outlined" onClick={logout} className={s.disconnectButton} size={'240'}>
                      Disconnect
                    </CustomButton>
                  </>
                )}
                {web3token && arcanaAcc && (
                  <>
                    <CustomButton
                      variant="outlined"
                      onClick={() => {
                        setIsOpen(false)
                        navigate('../settings')
                      }}
                      size="240"
                      className={s.disconnectButton}
                    >
                      <img className={s.metamaskIcon} src={arcanaIcon} alt="" />
                      <span className={s.cropTextButton}>{arcanaAcc}</span>
                    </CustomButton>
                    <CustomButton
                      variant="outlined"
                      onClick={handleArcanaLogout}
                      className={`${s.disconnectButton}`}
                      size={'240'}
                      disabled={logoutLoading}
                    >
                      Disconnect
                    </CustomButton>
                  </>
                )}
              </div>
            </div>
          )}
          <div className={s.sidebarFooter}>
            <ul className={s.socialList}>
              <li className={s.socialList__item}>
                <a href={'https://twitter.com/ads_meta'} target="_blank" rel="noopener noreferrer">
                  <TwitterIcon className={s.socialIcon} />
                </a>
              </li>
              <li className={s.socialList__item}>
                <a href={'https://t.me/+rPEWHyKEYVZjMTI6'} target="_blank" rel="noopener noreferrer">
                  <TelegramIcon className={s.socialIcon} />
                </a>
              </li>
              <li className={s.socialList__item}>
                <a href={'https://github.com/MetaAdsTeam'} target="_blank" rel="noopener noreferrer">
                  <GithubIcon className={s.socialIcon} />
                </a>
              </li>
              <li className={s.socialList__item}>
                <a
                  href="https://www.linkedin.com/company/metaadsteam/mycompany/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedInIcon className={s.socialIcon} />
                </a>
              </li>
              <li className={s.socialList__item}>
                <a href="https://www.youtube.com/@metaads4501" target="_blank" rel="noopener noreferrer">
                  <YoutubeIcon className={s.socialIcon} />
                </a>
              </li>
              <li className={s.socialList__item}>
                <a href="https://medium.com/@Meta_Ads" target="_blank" rel="noopener noreferrer">
                  <MediumIcon className={s.socialIcon} />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Sidebar
